import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Lock, Unlock, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/constants";
import ReferralModal from "@/components/ReferralModal";

/** Phase 1 — the standardised APPROVE / REJECT / REWORK action row plus
 *  the Restricted toggle and Refer-a-clause button. Rendered inside the
 *  Legal action bar on Contract Detail. */
const LEGAL = ["general_counsel", "legal_manager", "attorney", "contract_analyst", "it_admin"];
const RESTRICT_TOGGLE_ROLES = ["general_counsel", "legal_manager", "it_admin"];

export default function ContractActionsBar({ contractId, contract, onDone }) {
  const { user } = useAuth();
  const canAct = hasRole(user, LEGAL);
  const canRestrict = hasRole(user, RESTRICT_TOGGLE_ROLES);
  const [busy, setBusy] = useState(false);
  const [showRework, setShowRework] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [comment, setComment] = useState("");
  const [referralOpen, setReferralOpen] = useState(false);

  if (!canAct) return null;

  const fire = async (action, cmt) => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contractId}/action`, { action, comment: cmt || "" });
      toast.success(action === "approve" ? "Advanced" : action === "reject" ? "Rejected" : "Returned for rework");
      setShowRework(false); setShowReject(false); setComment("");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
    finally { setBusy(false); }
  };

  const toggleRestricted = async () => {
    const nextVal = !contract?.is_restricted;
    let reason = "";
    if (nextVal) {
      reason = window.prompt("Reason for marking this contract as Restricted (e.g., executive employment, managed care exemption):");
      if (reason === null) return;
    } else {
      if (!window.confirm("Remove the Restricted tag from this contract?")) return;
    }
    try {
      await api.post(`/contracts/${contractId}/restricted`, { is_restricted: nextVal, reason });
      toast.success(nextVal ? "Marked as Restricted" : "Restricted tag removed");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <button onClick={() => fire("approve")} disabled={busy || contract?.stage === "executed"}
              data-testid="btn-action-approve"
              className="h-8 px-3 text-xs font-bold text-white rounded flex items-center gap-1 disabled:opacity-40"
              style={{ background: "#00AE42" }}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
      </button>
      <button onClick={() => setShowRework(true)} disabled={busy || contract?.stage === "executed"}
              data-testid="btn-action-rework"
              className="h-8 px-3 text-xs font-bold rounded flex items-center gap-1 border border-amber-600 text-amber-700 hover:bg-amber-50 disabled:opacity-40">
        <RotateCcw className="w-3.5 h-3.5" /> Rework
      </button>
      <button onClick={() => setShowReject(true)} disabled={busy || contract?.stage === "executed"}
              data-testid="btn-action-reject"
              className="h-8 px-3 text-xs font-bold rounded flex items-center gap-1 border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-40">
        <XCircle className="w-3.5 h-3.5" /> Reject
      </button>
      <button onClick={() => setReferralOpen(true)}
              data-testid="btn-refer-clause"
              className="h-8 px-3 text-xs font-semibold rounded flex items-center gap-1 border border-[#005289] text-[#005289] hover:bg-slate-50">
        <MessageSquare className="w-3.5 h-3.5" /> Refer clause
      </button>
      {canRestrict && (
        <button onClick={toggleRestricted}
                data-testid="btn-toggle-restricted"
                className={`h-8 px-3 text-xs font-semibold rounded flex items-center gap-1 border ${contract?.is_restricted ? "border-red-600 text-red-700 bg-red-50" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          {contract?.is_restricted ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          {contract?.is_restricted ? "Restricted" : "Mark Restricted"}
        </button>
      )}

      {(showRework || showReject) && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4"
             onClick={() => { setShowRework(false); setShowReject(false); }}
             data-testid={showRework ? "rework-modal" : "reject-modal"}>
          <div className="bg-white rounded-md w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="font-bold text-sm text-slate-900 mb-3">
              {showRework ? "Return for rework" : "Reject contract"}
            </div>
            <div className="text-xs text-slate-500 mb-2">
              {showRework
                ? "The submitter will be able to edit any intake field and re-upload documents. Please explain what's needed."
                : "The contract will be marked as rejected. Please provide a reason (audit-logged)."}
            </div>
            <textarea value={comment} onChange={e => setComment(e.target.value)}
                      data-testid="action-comment" rows={5} maxLength={4000}
                      placeholder="Comment (required)"
                      className="w-full p-3 text-sm border border-slate-200 rounded resize-none focus:border-[#005289] focus:outline-none" />
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => { setShowRework(false); setShowReject(false); setComment(""); }}
                      className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={() => fire(showRework ? "rework" : "reject", comment)}
                      disabled={busy || !comment.trim()}
                      data-testid="action-submit"
                      className={`ml-auto h-9 px-4 text-xs font-bold text-white rounded disabled:opacity-40 ${showRework ? "bg-amber-600" : "bg-red-600"}`}>
                {busy ? "Submitting…" : (showRework ? "Return for Rework" : "Reject")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReferralModal
        open={referralOpen}
        contract={contract}
        onClose={() => setReferralOpen(false)}
        onCreated={onDone}
      />
    </>
  );
}
